import React, { useState, useEffect, useContext, useReducer, useMemo } from 'react'

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    gridClasses,
    LicenseInfo,
    ptBR
} from '@mui/x-data-grid-pro';

import {
    createTheme,
    ThemeProvider,
    StyledEngineProvider
} from '@mui/material/styles';

import { ptBR as corePtBR } from '@mui/material/locale';

import { Box, Tooltip, Button, IconButton, Select, MenuItem, InputLabel, FormControl } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import useNotification from '../../../Notification';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../../../../api';

import Loading from '../../../Loading';
import userPermissions from '../../../User/Permissions';
import { getSession } from '../../../../auth';
import InfoIcon from '@mui/icons-material/Info';

import {
    stringOperators,
    choiceOperators
} from "../../../../utils/filterOperators";
import { lockedReducer } from '../../../../utils/defaultReducer';
import { number2dayweek } from '../../../Report/NewDetailedRegister/detailed_register_leftmenu';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ptBR as adapterptBR } from 'dayjs/locale/pt-br'
import { ptBR as localePTBR } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs';
import LoadingButton from '@mui/lab/LoadingButton';
import TableRowsIcon from '@mui/icons-material/TableRows'
import AuditInformation from './edit';


const theme = createTheme({
    palette: {
        primary: { main: '#21446C' },
    },
}, ptBR, corePtBR);

const method_dict = {
    'GET': 'Visualizar',
    'POST': 'Criar',
    'PATCH': 'Atualizar',
    'DELETE': 'Excluir'
}

const defColumns = (setState) => [
    { field: 'username', headerName: 'Operador', flex: 1 },
    { field: 'timestamp', headerName: 'Data', flex: 1, renderCell: ({ value }) => dayjs.utc(value).local().format('DD/MM/YYYY HH:mm:ss') },
    { field: 'true_function', headerName: 'Função', flex: 1, renderCell: ({value}) => value.name },
    { field: 'method', headerName: 'Método', flex: 1, renderCell: ({ value }) => method_dict[value] },
    { field: 'ip', headerName: 'IP', flex: 1 },
    {
        field: 'action', headerName: 'Informações', width: 120,
        renderCell: ({ row }) => <IconButton onClick={() => setState({ info: row })}><InfoIcon /></IconButton>
    },
]

const get_rows = async (setState, limit = 25, page = 0, filters) => {
    setState({ loading: true })
    const enterpriseId = getSession().profile.enterprise.id
    const offset = limit * (page)

    const urlfilters = Object.entries(filters || {}).flatMap(([key, value]) => {
        if (value.length === 0)
            return []
        else if (key === 'timestamp__after')
            return `${key}=${dayjs.utc(value).format('YYYY-MM-DD HH:mm:ss')}`
        else if (key === 'timestamp__before')
            return `${key}=${dayjs.utc(value).format('YYYY-MM-DD HH:mm:ss')}`
        return `${key}=${value}`
    }).join('&')

    const res = await api.get(`api/enterprises/${enterpriseId}/audit/?limit=${limit}&offset=${offset}&${urlfilters}`)
    if (res.status === 200) {
        setState({
            count: res.data.count,
            rows: res.data.results,
            loading: false,
            limit: limit,
            page: page
        })
    } else {
        setState({ loading: false })
        useNotification(
            "Ops",
            "Não foi possível recuperar os dados.",
            "danger"
        )
    }
}

const getUsersEmails = async (setState) => {
    setState({loading: true})
    const enterpriseId = getSession().profile.enterprise.id
    const [users, functions] = await Promise.all([api.get(`/api/enterprises/${enterpriseId}/users/get_users_emails/`),
         api.get(`/api/enterprises/${enterpriseId}/audit/retrieve_functions/`)])
    const response = {loading: false}
    if (users.status === 200)
        response.users = users.data
    if (functions.status === 200)
        response.functions = functions.data
    setState(response)
}

const initial_limit = 25

const Audit = ({ enterpriseId }) => {

    const groups = userPermissions();
    const [state, setState] = useReducer(lockedReducer, {
        rows: [],
        loading: false,
        info: null,
        limit: initial_limit,
        page: 0,
        count: 0,
        users: [],
        functions: []
    })

    const [filters, setFilters] = useReducer(lockedReducer, {
        timestamp__after: dayjs().subtract(1, 'month'),
        timestamp__before: dayjs(),
        user__in: [],
        function__in: [],
        method__in: [],
    })

    useEffect(()=>{
        getUsersEmails(setState)
    }, [])

    return (
        <>
            <Loading loading={state.loading} />
            {state.info && <AuditInformation data={state.info} handleClose={() => setState({ info: null })} />}
            <Box sx={{
                height: "100%",
                display: "flex",
                gap: ".5rem",
                flexDirection: "column",
                padding: '.5rem',
                border: '1px solid #e1e1e1',
                borderRadius: '.25rem'
            }
            }>
                <LocalizationProvider
                    sx={{ justifyContent: 'space-between' }}
                    dateAdapter={AdapterDayjs}
                    adapterLocale={adapterptBR}
                    localeText={
                        localePTBR.components.MuiLocalizationProvider.defaultProps.localeText
                    }
                >
                    <Box sx={{ display: 'flex', gap: '1rem', }}>
                        <DateTimePicker
                            sx={{ width: '100%', maxWidth: '13rem' }}
                            label={'De'}
                            views={['day', 'month', 'year', 'hours', 'minutes']}
                            format={'DD/MM/YYYY HH:mm'}
                            slotProps={{ textField: { size: 'small' } }}
                            value={filters.timestamp__after}
                            onChange={e => setFilters({timestamp__after: e})}
                            ampm={false}
                            dayOfWeekFormatter={(n, date) => number2dayweek[date.$W]}
                        />
                        <DateTimePicker
                            sx={{ width: '100%', maxWidth: '13rem' }}
                            label={'Até'}
                            views={['day', 'month', 'year', 'hours', 'minutes']}
                            format={'DD/MM/YYYY HH:mm'}
                            slotProps={{ textField: { size: 'small' } }}
                            value={filters.timestamp__before}
                            onChange={e => setFilters({timestamp__before: e})}
                            ampm={false}
                            dayOfWeekFormatter={(n, date) => number2dayweek[date.$W]}
                        />
                        <FormControl size='small' sx={{ minWidth: '10rem', maxWidth: '10rem' }}>
                            <InputLabel id='report-type-user'>Usuário(s)</InputLabel>
                            <Select
                                multiple={true}
                                labelId='report-type-user'
                                id='report-type-user-select'
                                value={filters.user__in}
                                label='Usuário(s)'
                                onChange={e => setFilters({ user__in: e.target.value })}
                            >
                                {state.users.map((user) => (
                                    <MenuItem value={user.id}>{user.first_name} {user.last_name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size='small' sx={{ minWidth: '10rem', maxWidth: '10rem' }}>
                            <InputLabel id='report-type-function__in'>Função(s)</InputLabel>
                            <Select
                                multiple={true}
                                labelId='report-type-function__in'
                                id='report-type-function__in-select'
                                value={filters.function__in}
                                label='Função(s)'
                                onChange={e => setFilters({ function__in: e.target.value })}
                            >
                                {Object.entries(state.functions).map(([key, value]) => (
                                    <MenuItem value={key}>{value?.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size='small' sx={{ minWidth: '10rem', maxWidth: '10rem' }}>
                            <InputLabel id='report-type-method__in'>Método(s)</InputLabel>
                            <Select
                                multiple={true}
                                labelId='report-type-method__in'
                                id='report-type-method__in-select'
                                value={filters.method__in}
                                label='Método(s)'
                                onChange={e => setFilters({ method__in: e.target.value })}
                            >
                                {Object.entries(method_dict).map(([key, value]) => (
                                    <MenuItem value={key}>{value}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <LoadingButton
                            onClick={() => {
                                get_rows(setState, state.limit, state.page, filters)
                            }}
                            loadingPosition='start'
                            loading={false}
                            variant='contained'
                            startIcon={<TableRowsIcon />}
                        >
                            Pesquisar
                        </LoadingButton>
                    </Box>
                </LocalizationProvider >
                <Box className="settings-body-bot">
                    <StyledEngineProvider injectFirst >
                        <ThemeProvider theme={theme}>
                            <DataGridPro

                                disableSelectionOnClick
                                disableDensitySelector
                                density="compact"
                                columns={useMemo(() => defColumns(setState), [])}
                                rows={state.rows}

                                //pagination
                                pagination
                                rowCount={state.count}
                                paginationMode="server"
                                rowsPerPageOptions={[25, 50, 100]}
                                page={state.page}

                                onPageSizeChange={(size) => { get_rows(setState, size, state.page, filters) }}
                                onPageChange={(page) => { get_rows(setState, state.limit, page, filters) }}

                                initialState={{
                                    pagination: {
                                        pageSize: Number(initial_limit)
                                    }
                                }}
                            />
                        </ThemeProvider>
                    </StyledEngineProvider>
                </Box>
            </Box >
        </>
    )
}

export default Audit